import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { OnboardingProfile } from './types';

interface UpdateOnboardingResponse {
  ok: boolean;
  completionPercent: number;
}

/**
 * Saves a partial edit to an employee's onboarding/e-joining profile.
 * Authorized server-side for superadmin, admin, or HR-department users only
 * (see PUT /api/hr/people/by-user/:userId/onboarding). Invalidates every
 * cached Employee 360 query for this user so the read view refreshes.
 */
export function useUpdateOnboardingProfile(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<OnboardingProfile>) => {
      const { data } = await api.put<UpdateOnboardingResponse>(
        `/api/hr/people/by-user/${encodeURIComponent(userId!)}/onboarding`,
        patch,
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hr', 'employee-360', userId] }),
  });
}
